import type { AppDispatch, RootState } from "@store/index";
import { datasetApi } from "./dataset-api";
import {
  reconcileMetricUsage,
  type WidgetMetricMembership,
} from "@utils/dataset-metric-usage";
import { serializeMetricsConfigJson } from "@models/Metric";

// The chartSourceType/datasetMetricId branch below covers any chart type
// using that single-top-level-field shape (Bar Chart, Line Chart) — no
// chart-type check needed since the field names are identical by design.
export function extractDatasetMetricIds(configJson?: string | null): string[] {
  if (!configJson) return [];
  try {
    const config = JSON.parse(configJson);

    if (Array.isArray(config?.rows)) {
      const ids: string[] = config.rows
        .filter(
          (row: unknown): row is { type: string; datasetMetricId: string } =>
            !!row &&
            typeof row === "object" &&
            (row as { type?: unknown }).type === "metric" &&
            typeof (row as { datasetMetricId?: unknown }).datasetMetricId === "string",
        )
        .map((row: { datasetMetricId: string }) => row.datasetMetricId);
      return [...new Set(ids)];
    }

    if (
      config?.chartSourceType === "metric" &&
      typeof config?.datasetMetricId === "string" &&
      config.datasetMetricId
    ) {
      return [config.datasetMetricId];
    }

    return [];
  } catch {
    return [];
  }
}

/**
 * Reconciles widget→metric membership for one dataset and PATCHes its
 * metricsConfigJson only when usage actually changed. Best-effort: any
 * failure is swallowed so it never blocks the widget save/delete it follows.
 * Mirrors syncDatasetBaseUsage exactly (same race-window caveat: this is a
 * client-side read-modify-write, so concurrent writes to metricsConfigJson —
 * e.g. a researcher editing a metric's definition at the same moment another
 * widget saves — can drop one write. Advisory counts, not an invariant.)
 */
export async function syncDatasetMetricUsage(
  dispatch: AppDispatch,
  getState: () => RootState,
  datasetId: string | undefined | null,
  updates: WidgetMetricMembership[],
): Promise<void> {
  if (!datasetId || updates.length === 0) return;

  let dataset;
  try {
    dataset = await dispatch(
      datasetApi.endpoints.getDatasetById.initiate(datasetId, {
        forceRefetch: true,
        subscribe: false,
      }),
    ).unwrap();
  } catch {
    return;
  }

  const { metrics: nextMetrics, changed } = reconcileMetricUsage(
    dataset.metrics ?? [],
    updates,
  );
  if (!changed) return;

  try {
    await dispatch(
      datasetApi.endpoints.patchDataset.initiate({
        id: datasetId,
        metricsConfigJson: serializeMetricsConfigJson(nextMetrics),
      }),
    ).unwrap();
  } catch {
    // best-effort — usage counts are advisory, not an invariant
  }
}

interface RemovableWidget {
  id: string;
  datasetId?: string | null;
  configJson?: string | null;
}

/**
 * Cascade removal (tab/dashboard delete): drops many widgets from their
 * metrics, grouped by dataset so each affected dataset gets a single
 * reconciling PATCH.
 */
export async function syncRemovedWidgetsMetricUsage(
  dispatch: AppDispatch,
  getState: () => RootState,
  dashboardId: string,
  widgets: RemovableWidget[],
): Promise<void> {
  const byDataset = new Map<string, WidgetMetricMembership[]>();
  for (const widget of widgets) {
    if (!widget.datasetId) continue;
    const updates = byDataset.get(widget.datasetId) ?? [];
    updates.push({ widgetId: widget.id, dashboardId, metricIds: [] });
    byDataset.set(widget.datasetId, updates);
  }

  await Promise.all(
    Array.from(byDataset.entries()).map(([datasetId, updates]) =>
      syncDatasetMetricUsage(dispatch, getState, datasetId, updates),
    ),
  );
}
